/**
 * SkupinyModal — firma groups (Platforma → Firmy → Skupiny), platform admin only.
 *
 * A group is a trust circle: firmas in one group share a narrow read-only view of
 * each other's cars (servis + uskladnění pickers) and write STK / servis dates
 * back to the owner. Creating, renaming and deleting groups lives here; which
 * firma sits in which group is set on the firma itself (CompanyFormModal →
 * "Skupina firem"). A group can only be deleted once it is empty.
 */

import { useState } from 'react';
import { Button } from '@/components/Button';
import { useConfirm } from '@/components/ConfirmDialog';
import { Field, Fields } from '@/components/Field';
import { Icon } from '@/components/Icon';
import { IconButton } from '@/components/IconButton';
import { Modal, DialogCrown } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import {
  useCreateSkupinaMutation,
  useDeleteSkupinaMutation,
  useRenameSkupinaMutation,
  useSkupinyQuery,
  type MgmtSkupina,
} from './api';

interface SkupinyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function apiMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const e = (err.body as { error?: string }).error;
    if (typeof e === 'string') return e;
  }
  return err instanceof Error ? err.message : fallback;
}

export function SkupinyModal({ open, onOpenChange }: SkupinyModalProps) {
  const list = useSkupinyQuery(open);
  const create = useCreateSkupinaMutation();
  const rename = useRenameSkupinaMutation();
  const remove = useDeleteSkupinaMutation();
  const { confirm, dialog: confirmDialog } = useConfirm();

  const [novy, setNovy] = useState('');
  const [editing, setEditing] = useState<{ id: number; nazev: string } | null>(null);

  const skupiny = list.data?.skupiny ?? [];

  const add = () => {
    const nazev = novy.trim();
    if (!nazev) return;
    create.mutate(
      { nazev },
      {
        onSuccess: () => {
          setNovy('');
          toast.ok('Skupina vytvořena');
        },
        onError: (err) => toast.error(apiMessage(err, 'Skupinu se nepodařilo vytvořit.')),
      },
    );
  };

  const saveRename = () => {
    if (!editing) return;
    const nazev = editing.nazev.trim();
    if (!nazev) return;
    rename.mutate(
      { id: editing.id, nazev },
      {
        onSuccess: () => setEditing(null),
        onError: (err) => toast.error(apiMessage(err, 'Skupinu se nepodařilo přejmenovat.')),
      },
    );
  };

  const del = async (s: MgmtSkupina) => {
    if (
      !(await confirm({
        title: (
          <>
            Smazat skupinu <em>{s.nazev}</em>?
          </>
        ),
        description: 'Skupina je prázdná, smaže se bez dopadu na vozidla.',
        tone: 'danger',
        confirmLabel: 'Smazat',
      }))
    ) {
      return;
    }
    remove.mutate(s.id, {
      onSuccess: () => toast.ok('Skupina smazána'),
      onError: (err) => toast.error(apiMessage(err, 'Skupinu se nepodařilo smazat.')),
    });
  };

  return (
    <>
      <Modal open={open} onOpenChange={onOpenChange} maxWidth={640}>
        <DialogCrown
          tone="neutral"
          eyebrow="Skupiny firem"
          title={
            <>
              Firmy, které si <em>věří</em>.
            </>
          }
          sub="Firmy ve stejné skupině sdílejí jen pro čtení vozy v servisu a uskladnění. Firmu do skupiny zařadíte v jejím detailu."
        />
        <Modal.Body className="px-8 py-6" data-testid="skupiny-modal">
          {list.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : skupiny.length === 0 ? (
            <p className="font-mono text-[12px] text-ink-3">Zatím žádná skupina.</p>
          ) : (
            <ul className="divide-y divide-line border border-line bg-paper">
              {skupiny.map((s) => (
                <li key={s.id} className="flex items-center gap-3 px-4 py-3" data-testid={`skupina-${s.id}`}>
                  <div className="min-w-0 flex-1">
                    {editing?.id === s.id ? (
                      <TextInput
                        value={editing.nazev}
                        maxLength={100}
                        autoFocus
                        onChange={(e) => setEditing({ id: s.id, nazev: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveRename();
                          if (e.key === 'Escape') {
                            e.stopPropagation();
                            setEditing(null);
                          }
                        }}
                      />
                    ) : (
                      <div className="truncate text-[13px] text-ink">{s.nazev}</div>
                    )}
                    <div className="mt-0.5 truncate font-mono text-[10.5px] text-ink-3">
                      {s.firmy.length === 0 ? 'bez členů' : s.firmy.map((f) => f.Nazev).join(' · ')}
                    </div>
                  </div>
                  {editing?.id === s.id ? (
                    <>
                      <Button size="sm" variant="secondary" onClick={() => setEditing(null)}>
                        Zrušit
                      </Button>
                      <Button size="sm" variant="ink" onClick={saveRename} disabled={rename.isPending}>
                        Uložit
                      </Button>
                    </>
                  ) : (
                    <>
                      <IconButton
                        size="sm"
                        aria-label={`Přejmenovat skupinu ${s.nazev}`}
                        onClick={() => setEditing({ id: s.id, nazev: s.nazev })}
                      >
                        <Icon name="edit" size={13} />
                      </IconButton>
                      <IconButton
                        size="sm"
                        aria-label={`Smazat skupinu ${s.nazev}`}
                        title={s.firmy.length > 0 ? 'Skupina má členy — nejdřív z ní firmy odeberte.' : undefined}
                        disabled={s.firmy.length > 0 || remove.isPending}
                        onClick={() => void del(s)}
                      >
                        <Icon name="close" size={13} />
                      </IconButton>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-6">
            <Fields cols={1}>
              <Field label="Nová skupina">
                <TextInput
                  data-testid="skupina-novy-nazev"
                  value={novy}
                  maxLength={100}
                  placeholder="např. Auto Trutnov + CEREBRUS"
                  onChange={(e) => setNovy(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') add();
                  }}
                />
              </Field>
            </Fields>
          </div>
        </Modal.Body>
        <Modal.Foot
          meta="Esc · zavřít"
          actions={
            <>
              <Button variant="secondary" size="md" onClick={() => onOpenChange(false)}>
                Zavřít
              </Button>
              <Button
                variant="primary"
                size="md"
                data-testid="skupina-pridat"
                onClick={add}
                disabled={!novy.trim() || create.isPending}
              >
                Vytvořit skupinu
              </Button>
            </>
          }
        />
      </Modal>
      {confirmDialog}
    </>
  );
}
